import { Link, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ReactNode } from 'react';
import { Address } from '@wwf/sdk';

interface AddressLinkProps {
    address: string | Address | null | undefined;
    variant?:
        | 'body1'
        | 'body2'
        | 'caption'
        | 'h1'
        | 'h2'
        | 'h3'
        | 'h4'
        | 'h5'
        | 'h6'
        | 'inherit'
        | 'overline'
        | 'subtitle1'
        | 'subtitle2';
    fallbackText?: string;
    children?: ReactNode;
    maxWidth?: number;
}

const formatAddress = (
    address: string | Address | null | undefined
): string => {
    if (!address) {
        return '';
    }

    if (typeof address === 'string') {
        return address;
    }

    // Handle Address JSON object
    const addressParts = [
        address.street,
        address.postal_code,
        address.city,
        address.country,
    ].filter(Boolean);

    return addressParts.join(', ');
};

export const AddressLink = ({
    address,
    variant = 'body2',
    fallbackText = 'No address',
    children,
    maxWidth,
}: AddressLinkProps) => {
    const formattedAddress = formatAddress(address);

    if (!formattedAddress) {
        return <Typography variant={variant}>{fallbackText}</Typography>;
    }

    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(formattedAddress)}`;

    return (
        <Typography variant={variant}>
            <Link
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                    color: 'primary.main',
                    textDecoration: 'none',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    maxWidth: maxWidth,
                    '&:hover': {
                        textDecoration: 'underline',
                    },
                }}
            >
                {children || formattedAddress}
                <OpenInNewIcon sx={{ fontSize: '0.875em' }} />
            </Link>
        </Typography>
    );
};
